"use client";

// Contents list of a long post: a sticky column with scroll-spy on wide
// screens, a collapsed list on phones and tablets.
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

export interface TocEntry {
  id: string;
  text: string;
}

export function TocDesktop({ entries }: { entries: TocEntry[] }) {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const headings = entries
      .map((e) => document.getElementById(e.id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (!headings.length) return;
    // The current section is the last heading above the top fifth of the screen.
    const update = () => {
      const line = window.innerHeight * 0.2;
      let current: string | null = null;
      for (const h of headings) if (h.getBoundingClientRect().top <= line) current = h.id;
      setActive(current);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [entries]);

  return (
    <nav aria-label="Contents" className="blog-toc">
      <p className="mb-3 text-sm font-semibold text-foreground">Contents</p>
      <ul className="border-l border-border">
        {entries.map((e) => (
          <li key={e.id}>
            <a href={`#${e.id}`} aria-current={active === e.id ? "true" : undefined}>
              {e.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function TocMobile({ entries }: { entries: TocEntry[] }) {
  return (
    <details className="blog-toc-mobile blog-toc mt-6 rounded-2xl border border-border bg-card px-5 py-1 lg:hidden">
      <summary className="flex items-center justify-between py-3 text-sm font-semibold text-foreground">
        <span>Contents</span>
        <span className="flex items-center gap-2 font-normal text-muted-foreground">
          {entries.length} sections
          <ChevronDown aria-hidden className="blog-toc-chevron h-4 w-4 transition-transform" />
        </span>
      </summary>
      <ul className="mb-4 border-l border-border">
        {entries.map((e) => (
          <li key={e.id}>
            <a href={`#${e.id}`}>{e.text}</a>
          </li>
        ))}
      </ul>
    </details>
  );
}
